import authService from 'Services/api/auth.service'
import clientService from 'Services/api/client.service'
import userService from 'Services/api/user.service'
import httpRequest from 'httpRequest'
import SplashPage from 'pages/SplashPage'
import { useCallback, useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import {
    ACTION_CODE_STORAGE_KEY,
    COMPLETE_FETCH_PERMISSION,
} from 'store/actions/auth.action'
import { clearInvalidServerKey, hasServerKey } from 'store/authCommon'
import {
    VALIDATE_TOKEN_FAILURE,
    VALIDATE_REAL_CLIENT_TOKEN_SUCCESS,
    NO_TOKEN,
    VALIDATE_TOKEN_BEGIN,
    VALIDATE_NON_CLIENT_TOKEN_SUCCESS,
} from 'store/reducers/auth.reducer'
import { GET_CLIENT_LOADED } from 'store/reducers/client.reducer'
import { RefreshToken, Token } from 'types/storeTypes'
import cookies from 'utilities/cookie'
import { isUsingNonClientTokenType } from 'utilities/jwtHelper'
import { loadDecrypted } from 'utilities/storage'

enum AuthCheckState {
    Start,
    Checking,
    Done,
}

export function AppAuthProvider({ children }: { children: React.ReactNode }) {
    const dispatch = useDispatch()
    const [authCheckState, setAuthCheckState] = useState<AuthCheckState>(
        AuthCheckState.Start,
    )
    const loadPermission = useCallback(async () => {
        let actionCodes: number[] = []
        const actionCodeRaw = await loadDecrypted(ACTION_CODE_STORAGE_KEY)
        if (actionCodeRaw) actionCodes = JSON.parse(actionCodeRaw)
        else {
            console.error('invalid parse module action')

            // remove credential
            dispatch({ type: VALIDATE_TOKEN_FAILURE })
            httpRequest.deleteAuthorizationCookies()
            return
        }
        dispatch({ type: COMPLETE_FETCH_PERMISSION, payload: { actionCodes } })
    }, [dispatch])
    // load actionCode / dispatch `validateTokenSuccess`
    const finalizeCheck = useCallback(async () => {
        const userResult = await userService.getCurrentUserData()
        await loadPermission()
        dispatch({
            type: VALIDATE_REAL_CLIENT_TOKEN_SUCCESS,
            payload: {
                user: userResult.data,
            },
        })

        // get current client info
        const currentClient = await clientService.getCurrentClient()

        // load client
        dispatch({ type: GET_CLIENT_LOADED, payload: currentClient.data })
    }, [dispatch, loadPermission])

    const checkAuthOnFirstLoad = useCallback(async () => {
        const token = cookies.get(Token)
        const refreshToken = cookies.get(RefreshToken)

        if (!token) {
            dispatch({ type: NO_TOKEN })
            return
        }

        if (!refreshToken || isUsingNonClientTokenType() || !hasServerKey()) {
            // Non-Client Token Type

            dispatch({ type: VALIDATE_TOKEN_BEGIN })

            try {
                await authService.checkNonClientToken()

                dispatch({ type: VALIDATE_NON_CLIENT_TOKEN_SUCCESS })
                await loadPermission()
            } catch (err: any) {
                dispatch({ type: VALIDATE_TOKEN_FAILURE })
                httpRequest.deleteAuthorizationCookies()
            }
        } else {
            // Client Token Type (Real Token)
            dispatch({ type: VALIDATE_TOKEN_BEGIN })

            try {
                //we can change the method name for token validation.
                await authService.checkToken()

                await finalizeCheck()
            } catch (err: any) {
                console.log('Error during validate token: ' + err.message)
                dispatch({ type: VALIDATE_TOKEN_FAILURE })
                // clear token and refresh token from cookies if it fail to get version
                // because it mean that the token is invalid as well as the refresh token
                httpRequest.deleteAuthorizationCookies()
            }
        }
    }, [dispatch, finalizeCheck, loadPermission])

    const beginAuthorizationCheck = useCallback(async () => {
        await clearInvalidServerKey()
        await checkAuthOnFirstLoad()

        setAuthCheckState(AuthCheckState.Done)
    }, [checkAuthOnFirstLoad])

    useEffect(() => {
        beginAuthorizationCheck()
    }, [beginAuthorizationCheck])

    switch (authCheckState) {
        case AuthCheckState.Start:
        case AuthCheckState.Checking:
            return <SplashPage />
        case AuthCheckState.Done:
            return <>{children}</>
        default:
            return <div>authCheckState: Unhandle state</div>
    }
}
